import React, {
  useCallback, useEffect, useState, ReactElement,
} from 'react';
import Card from '../../../../components/card/card.component';
import Checkbox from '../../../../components/checkbox/checkbox.component';
import CheckboxLabel from '../../../../components/checkbox-label/checkbox-label.component';
import CheckboxColumn from '../../../../components/checkbox-column/checkbox-column.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import Grid from '../../../../components/grid/grid.component';
import GridItem from '../../../../components/grid-item/grid-item.component';
import Button from '../../../../components/button/button.component';
import ModalButtonBlock from '../../../../components/modal-button-block/modal-button-block.component';
import ModalLayout from '../../../../components/modal-layout/modal-layout.component';
import CheckAccess from '../../../../components/check-access/check-access.component';

const TableColumnsModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMN_SIZE_MEDIUM: number = 3;
  const ID_PREFIX_CHECKBOX_TABLE_COLUMNS_CONFIGURE_ITEM: string = 'checkbox-table-columns-configure-item';
  const ID_PREFIX_CHECKBOX_TABLE_COLUMNS_CONFIGURE_GROUP: string = 'checkbox-table-columns-configure-group';

  const {
    isOpen,
    isDisabled,
    tableFields,
    tableFieldGroups,
    tableColumnSettings,
    onCloseModal,
    onApplySettings,
  } = props;

  const isMedium: boolean = !!tableFieldGroups && tableFieldGroups.length >= COLUMN_SIZE_MEDIUM;

  const [currentTableColumnSettings, setCurrentTableColumnSettings]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);

  const applySettings: MouseEventHandlerFunction = useCallback((): void => {
    if (onApplySettings && currentTableColumnSettings) {
      onApplySettings([...currentTableColumnSettings]);
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, [currentTableColumnSettings]);

  const resetSettings: Function = useCallback((): void => {
    if (tableColumnSettings && !currentTableColumnSettings) {
      setCurrentTableColumnSettings([...tableColumnSettings]);
    }
  }, [tableColumnSettings]);

  const closeModal: Function = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }

    resetSettings();
  }, []);

  const onGroupItemClick: Function = useCallback((
    event: React.MouseEvent<HTMLLabelElement>,
    groupName: string,
    isVisible: boolean,
  ): void => {
    if (currentTableColumnSettings) {
      const groupTableFields: TableField[] = tableFields.filter((tableField: TableField): boolean => (
        tableField.group === groupName
      ));
      const groupTableFieldNames: string[] = groupTableFields.map((tableField: TableField): string => tableField.name);

      const newTableColumnSettings: TableField[] = currentTableColumnSettings.map((
        tableField: TableField,
      ): TableField => ({
        ...tableField,
        isVisible: groupTableFieldNames.includes(tableField.name)
          ? isVisible : tableField.isVisible,
      }));

      setCurrentTableColumnSettings([...newTableColumnSettings]);
    }

    event.preventDefault();
  }, [currentTableColumnSettings, tableFields]);

  const onItemClick: Function = useCallback((
    event: React.MouseEvent<HTMLLabelElement>,
    tableFieldName: string,
    isVisible: boolean,
  ): void => {
    if (currentTableColumnSettings) {
      const newTableColumnSettings: TableField[] = currentTableColumnSettings.map((
        tableField: TableField,
      ): TableField => ({
        ...tableField,
        isVisible: tableFieldName === tableField.name ? isVisible : tableField.isVisible,
      }));

      setCurrentTableColumnSettings([...newTableColumnSettings]);
    }

    event.preventDefault();
  }, [currentTableColumnSettings]);

  useEffect((): void => {
    resetSettings();
  }, [tableColumnSettings]);

  return (
    <ModalLayout isOpen={isOpen} isMedium={isMedium} title="Columns" onCloseModal={closeModal}>
      <Card isContainer>
        <Grid isCentered>
          {
            tableFieldGroups.map((tableFieldGroupItem: TableFieldGroup): ReactElement | boolean => {
              const groupTableFields: TableField[] = tableFields.filter((tableField: TableField): boolean => (
                tableField.group === tableFieldGroupItem.name
              ));
              const groupTableFieldNames: string[] = groupTableFields.map((tableField: TableField): string => (
                tableField.name
              ));
              const groupTableColumnSettings: TableField[] | null = currentTableColumnSettings
                && currentTableColumnSettings.filter((tableColumnSetting: TableField): boolean => (
                  groupTableFieldNames.includes(tableColumnSetting.name)
                ));
              const isGroupVisible: boolean = groupTableColumnSettings ? groupTableColumnSettings.reduce((
                isVisible: boolean, tableField: TableField,
              ): boolean => (
                isVisible && !!tableField.isVisible
              ), true) : false;
              const isGroupHidden: boolean = groupTableColumnSettings ? groupTableColumnSettings.reduce((
                isVisible: boolean, tableField: TableField,
              ): boolean => (
                isVisible && !tableField.isVisible
              ), true) : true;

              return (
                <GridItem key={tableFieldGroupItem.id}>
                  <CheckboxColumn>
                    <FlexContainer>
                      <CheckboxLabel
                        isDark
                        isFullWidth
                        label={tableFieldGroupItem.title}
                        onClick={(event: React.MouseEvent<HTMLLabelElement>): void => {
                          onGroupItemClick(event, tableFieldGroupItem.name, !isGroupVisible);
                        }}
                      >
                        <Checkbox
                          isLight
                          isDisabled={isDisabled}
                          isIndeterminate={!isGroupVisible && !isGroupHidden}
                          isChecked={!isGroupHidden}
                          id={`${ID_PREFIX_CHECKBOX_TABLE_COLUMNS_CONFIGURE_GROUP}-${tableFieldGroupItem.id}`}
                        />
                      </CheckboxLabel>
                    </FlexContainer>
                    {
                      groupTableFields && !!groupTableFields.length
                      && groupTableFields.map((tableField: TableField): ReactElement | boolean => {
                        const tableColumnSetting: TableField | undefined = currentTableColumnSettings
                          ? currentTableColumnSettings.find((settingItem: TableField): boolean => (
                            settingItem.name === tableField.name
                          )) : undefined;

                        return !!tableColumnSetting && (
                          <CheckAccess
                            key={tableField.id}
                            permissions={tableField.permission ? [tableField.permission] : []}
                          >
                            <FlexContainer>
                              <CheckboxLabel
                                label={tableField.title}
                                onClick={(event: React.MouseEvent<HTMLLabelElement>): void => {
                                  onItemClick(event, tableField.name, !tableColumnSetting.isVisible);
                                }}
                              >
                                <Checkbox
                                  isDisabled={isDisabled}
                                  isChecked={tableColumnSetting.isVisible}
                                  id={`${ID_PREFIX_CHECKBOX_TABLE_COLUMNS_CONFIGURE_ITEM}-${tableField.id}`}
                                />
                              </CheckboxLabel>
                            </FlexContainer>
                          </CheckAccess>
                        );
                      })
                    }
                  </CheckboxColumn>
                </GridItem>
              );
            })
          }
        </Grid>
      </Card>
      <ModalButtonBlock isFixed isFixedMedium={isMedium}>
        <Button isActive onClick={applySettings}>
          Apply
        </Button>
      </ModalButtonBlock>
    </ModalLayout>
  );
});

export default TableColumnsModal;
